import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { getRequestErrorMessage, pushNotify } from '../../../../../../components/Notifications';
import { useLazyDownloadSettlementsQuery } from '../../../../../../redux/services/SettlementsApi';
import { useEscapeKey } from '../../../../../../utils/hooks/useEscapeKey';
import { saveBlob } from '../../../../../../utils/functions/saveBlob';
import { Selector, TItem } from './SelectType';
import styles from './DownloadSettlements.module.css';

interface Inputs {
  direction: 'all' | 'M2P' | 'P2M';
  status: 'all' | 'created' | 'in_progress' | 'rejected' | 'completed';
  date_from: string;
  date_to: string;
}

const emptyForm: Inputs = { direction: 'all', status: 'all', date_from: '', date_to: '' };

const directions: TItem<'all' | 'M2P' | 'P2M'>[] = [
  { id: 'all', name: 'Все' },
  { id: 'M2P', name: 'Мерчант → Площадка' },
  { id: 'P2M', name: 'Площадка → Мерчант' },
];
const statuses: TItem<'all' | 'created' | 'in_progress' | 'rejected' | 'completed'>[] = [
  { id: 'all', name: 'Все' },
  { id: 'created', name: 'Создано' },
  { id: 'in_progress', name: 'В процессе' },
  { id: 'rejected', name: 'Отклонено' },
  { id: 'completed', name: 'Завершено' },
];

export const DownloadSettlements: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const methods = useForm<Inputs>({ defaultValues: emptyForm });
  const { handleSubmit, watch, setValue, register, reset } = methods;
  const [trigger, { isFetching }] = useLazyDownloadSettlementsQuery();
  const [rangeError, setRangeError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      reset(emptyForm);
      setRangeError(null);
    }
  }, [open, reset]);
  useEscapeKey(open && !isFetching, onClose);

  const onSubmit = async (data: Inputs) => {
    if (data.date_from && data.date_to && data.date_from > data.date_to) {
      setRangeError('Дата «с» не может быть позже даты «по».');
      return;
    }
    setRangeError(null);
    try {
      const blob = await trigger({
        direction: data.direction,
        status: data.status,
        date_from: data.date_from || undefined,
        date_to: data.date_to || undefined,
      }).unwrap();
      const ts = new Date().toISOString().replace(/[-:]/g, '').split('.')[0];
      saveBlob(blob, `Settlements_${ts}.xlsx`);
      pushNotify('Выписка скачана', 'Готово');
      onClose();
    } catch (error) {
      pushNotify(getRequestErrorMessage(error, 'Не удалось скачать выписку'), 'Ошибка');
    }
  };

  if (!open) return null;

  return createPortal(
    <FormProvider {...methods}>
      <div className={styles.backdrop} onClick={isFetching ? undefined : onClose} />
      <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="download-settlements-title" aria-busy={isFetching}>
        <div className={styles.header}>
          <h2 id="download-settlements-title" className={styles.title}>Скачать выписку</h2>
          <button type="button" aria-label="Закрыть" className={styles.closeBtn} onClick={onClose} disabled={isFetching}>×</button>
        </div>
        <div className={styles.subtitle}>Выберите направление, статус и период</div>
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          <Selector title="Направление" items={directions} value={watch('direction')} onChange={(v) => setValue('direction', v)} />
          <Selector title="Статус" items={statuses} value={watch('status')} onChange={(v) => setValue('status', v)} />
          <div className={styles.dateRow}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="settlements-date-from">Дата с</label>
              <input id="settlements-date-from" type="date" {...register('date_from')} className={styles.input} />
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="settlements-date-to">Дата по</label>
              <input id="settlements-date-to" type="date" {...register('date_to')} className={styles.input} />
            </div>
          </div>
          {rangeError && <p role="alert" className={styles.label}>{rangeError}</p>}
          <button type="submit" className={styles.button} disabled={isFetching || !watch('date_from') || !watch('date_to')}>
            {isFetching ? 'Готовим файл…' : 'Скачать'}
          </button>
        </form>
      </div>
    </FormProvider>,
    document.body,
  );
};
